import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { formatToDealerTime } from 'utils/dateUtils';

type ProgressSection = {
  index: number;
  type: string;
  main?: string;
  sub_text?: string;
};

type PlanDayTestProgressDialogProps = {
  open: boolean;
  onClose: () => void;
  activePlanDay: number | null;
  activeJobId: string | null;
  activeRunId: string | null;
  isFinal: boolean;
  phase: 'idle' | 'job' | 'run';
  currentSection?: ProgressSection;
  progressMessage: string;
  progressOverride?: number;
  latestJobPayload: Record<string, unknown> | null;
  latestRunEvent: Record<string, unknown> | null;
  errorMessage: string | null;
  canViewRunLogs: boolean;
  onViewRunLogs?: () => void;
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
      gap: 0.5,
      py: 0.75,
      borderBottom: theme => `1px solid ${theme.palette.divider}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" color="text.secondary" fontWeight={600}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const formatStatusLabel = (status?: unknown): string => {
  const raw = String(status ?? '').trim();
  if (!raw) return '—';
  return raw.replace(/_/g, ' ');
};

const statusChipColor = (
  status?: unknown,
): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'completed' || normalized === 'success') return 'success';
  if (normalized === 'failed' || normalized === 'error') return 'error';
  if (
    normalized === 'processing' ||
    normalized === 'running' ||
    normalized === 'queued' ||
    normalized === 'pending'
  ) {
    return 'primary';
  }
  return 'default';
};

const PlanDayTestProgressDialog = ({
  open,
  onClose,
  activePlanDay,
  activeJobId,
  activeRunId,
  isFinal,
  phase,
  currentSection,
  progressMessage,
  progressOverride,
  latestJobPayload,
  latestRunEvent,
  errorMessage,
  canViewRunLogs,
  onViewRunLogs,
}: PlanDayTestProgressDialogProps) => {
  const progressPercent = useMemo(() => {
    if (progressOverride != null && typeof progressOverride === 'number') {
      return Math.min(Math.max(progressOverride, 0), 100);
    }
    return 0;
  }, [progressOverride]);

  const activeStatus =
    phase === 'run'
      ? latestRunEvent?.status ?? latestRunEvent?.run_status
      : latestJobPayload?.status;

  const showSpinner = !isFinal && !errorMessage;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          maxHeight: '85vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {activePlanDay != null
              ? `Testing plan day ${activePlanDay}`
              : 'Plan day test progress'}
          </Typography>
          {activeStatus != null ? (
            <Chip
              size="small"
              label={formatStatusLabel(activeStatus)}
              color={statusChipColor(activeStatus)}
              sx={{ textTransform: 'capitalize' }}
            />
          ) : null}
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {showSpinner ? <CircularProgress size={16} thickness={5} /> : null}
            <Typography variant="body2" fontWeight={600}>
              {currentSection?.main || (phase === 'run' ? 'Run phase' : 'Job phase')}
            </Typography>
          </Box>
          {currentSection?.sub_text ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {currentSection.sub_text}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {progressMessage || 'Waiting for socket events...'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {progressPercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {latestJobPayload ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Job lifecycle
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <DetailRow label="Event" value="queue_job_lifecycle" />
              <DetailRow label="Job ID" value={String(latestJobPayload.job_id ?? activeJobId ?? '—')} />
              <DetailRow
                label="Job name"
                value={String(latestJobPayload.job_name ?? '—').replace(/_/g, ' ')}
              />
              <DetailRow
                label="Status"
                value={
                  <Chip
                    size="small"
                    label={formatStatusLabel(latestJobPayload.status)}
                    color={statusChipColor(latestJobPayload.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                }
              />
              <DetailRow label="Provider" value={String(latestJobPayload.provider ?? '—')} />
              <DetailRow
                label="Attempt"
                value={
                  latestJobPayload.attempt != null
                    ? `${latestJobPayload.attempt} / ${latestJobPayload.max_attempts ?? '—'}`
                    : '—'
                }
              />
              <DetailRow
                label="Timestamp"
                value={
                  latestJobPayload.timestamp
                    ? formatToDealerTime(String(latestJobPayload.timestamp))
                    : '—'
                }
              />
              <DetailRow label="Message" value={String(latestJobPayload.message ?? '—')} />
              {latestJobPayload.duration_ms != null ? (
                <DetailRow label="Duration" value={`${latestJobPayload.duration_ms} ms`} />
              ) : null}
              {latestJobPayload.error != null ? (
                <DetailRow
                  label="Error"
                  value={
                    <Typography variant="body2" color="error.main">
                      {String(latestJobPayload.error)}
                    </Typography>
                  }
                />
              ) : null}
            </Box>
          </Box>
        ) : activeJobId ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Job lifecycle
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <DetailRow label="Job ID" value={activeJobId} />
              <DetailRow label="Status" value="Waiting for socket update..." />
            </Box>
          </Box>
        ) : null}

        {activeRunId || latestRunEvent ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Run lifecycle
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <DetailRow label="Event" value="run_log_event" />
              <DetailRow
                label="Run ID"
                value={String(
                  latestRunEvent?.run_id ??
                    latestRunEvent?.run_log_id ??
                    latestRunEvent?.runId ??
                    activeRunId ??
                    '—',
                )}
              />
              <DetailRow
                label="Status"
                value={
                  latestRunEvent?.status != null ? (
                    <Chip
                      size="small"
                      label={formatStatusLabel(latestRunEvent.status)}
                      color={statusChipColor(latestRunEvent.status)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ) : (
                    '—'
                  )
                }
              />
              <DetailRow
                label="Message"
                value={String(latestRunEvent?.message ?? progressMessage ?? '—')}
              />
              {latestRunEvent?.progress != null ? (
                <DetailRow label="Run progress" value={`${latestRunEvent.progress}%`} />
              ) : null}
              {latestRunEvent?.processed_records != null ? (
                <DetailRow
                  label="Processed"
                  value={`${latestRunEvent.processed_records}${
                    latestRunEvent.total_records != null
                      ? ` / ${latestRunEvent.total_records}`
                      : ''
                  }`}
                />
              ) : null}
            </Box>
          </Box>
        ) : null}

        {errorMessage ? (
          <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Close
          </Button>
          {canViewRunLogs ? (
            <Button variant="contained" size="small" onClick={onViewRunLogs}>
              View Run Logs
            </Button>
          ) : null}
        </Box>
      </Paper>
    </Dialog>
  );
};

export default PlanDayTestProgressDialog;
